import React, { useLayoutEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

import addEmployeeImage from "../../assets/addemp.png";
import attendanceImage from "../../assets/attendance.png";
import payrollImage from "../../assets/payroll.png";

gsap.registerPlugin(ScrollTrigger);

export default function HowItWorks() {
  const sectionRef = useRef(null);
  const pathRef = useRef(null);
  const activeRef = useRef(0);

  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    {
      number: "01",
      title: "Add Employees",
      description:
        "Create employee profiles with salary structure, department, PF, ESI and PT applicability.",
      image: addEmployeeImage,
    },
    {
      number: "02",
      title: "Track Attendance",
      description:
        "Mark present, absent, half-days, paid leaves, LOP and overtime from a simple monthly calendar.",
      image: attendanceImage,
    },
    {
      number: "03",
      title: "Run Payroll",
      description:
        "Review salary, deductions and net pay. Confirm payroll, lock attendance and generate payslips.",
      image: payrollImage,
    },
  ];

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const path = pathRef.current;

      if (path) {
        const length = path.getTotalLength();

        gsap.set(path, {
          strokeDasharray: length,
          strokeDashoffset: length,
        });

        gsap.to(path, {
          strokeDashoffset: 0,
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top top",
            end: "+=1200",
            scrub: 0.7,
          },
        });
      }

      ScrollTrigger.create({
        trigger: sectionRef.current,
        start: "top top",
        end: "+=1200",
        scrub: true,
        pin: true,
        anticipatePin: 1,

        onUpdate: (self) => {
          let nextStep = 0;

          if (self.progress >= 0.68) {
            nextStep = 2;
          } else if (self.progress >= 0.34) {
            nextStep = 1;
          }

          if (activeRef.current !== nextStep) {
            activeRef.current = nextStep;
            setActiveStep(nextStep);
          }
        },
      });

      ScrollTrigger.refresh();
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} id="how-it-works" className="hiw-section">
      <div className="hiw-orb hiw-orb-left" />
      <div className="hiw-orb hiw-orb-right" />

      <div className="hiw-container">
        <div className="hiw-grid">
          {/* LEFT */}
          <div className="hiw-left">
            <motion.div
              className="hiw-pill"
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45 }}
            >
              How it works
            </motion.div>

            <motion.h2
              className="hiw-title"
              initial={{ opacity: 0, y: 34 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.35 }}
              transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
            >
              Get Started In
              <br />
              Minutes.
            </motion.h2>

            <div className="hiw-image-wrap">
              <AnimatePresence mode="wait">
                <motion.img
                  key={activeStep}
                  src={steps[activeStep].image}
                  alt={steps[activeStep].title}
                  className="hiw-image"
                  initial={{
                    opacity: 0,
                    y: 22,
                    scale: 0.965,
                    filter: "blur(8px)",
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    filter: "blur(0px)",
                  }}
                  exit={{
                    opacity: 0,
                    y: -18,
                    scale: 0.965,
                    filter: "blur(8px)",
                  }}
                  transition={{
                    duration: 0.45,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                />
              </AnimatePresence>
            </div>
          </div>

          {/* CENTER LINE */}
          <div className="hiw-middle">
            <svg
              className="hiw-line"
              viewBox="0 0 150 540"
              preserveAspectRatio="none"
            >
              <path
                ref={pathRef}
                d="M78 0
                   C36 115,
                   40 255,
                   82 372
                   C108 450,
                   104 505,
                   68 540"
                stroke="#cbd5e1"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
              />
            </svg>

            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                className={`hiw-dot hiw-dot-${index + 1} ${
                  activeStep === index ? "active" : ""
                }`}
                animate={{
                  scale: activeStep === index ? 1.12 : 1,
                }}
                transition={{
                  type: "spring",
                  stiffness: 260,
                  damping: 18,
                }}
              >
                {step.number}
              </motion.div>
            ))}
          </div>

          {/* RIGHT */}
          <div className="hiw-right">
            {steps.map((step, index) => {
              const isActive = activeStep === index;

              return (
                <motion.div
                  key={step.number}
                  className={`hiw-content hiw-content-${index + 1} ${
                    isActive ? "active" : ""
                  }`}
                  animate={{
                    opacity: isActive ? 1 : 0.34,
                    x: isActive ? 0 : 14,
                  }}
                  transition={{
                    duration: 0.42,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                >
                  <motion.span
                    className="hiw-arrow"
                    animate={{
                      opacity: isActive ? 1 : 0,
                      x: isActive ? 0 : -8,
                    }}
                    transition={{ duration: 0.32 }}
                  >
                    →
                  </motion.span>

                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}